<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import TableHeader from './TableHeader.vue';
import TableFilters from './TableFilters.vue';
import {
	ColumnInterface,
	TableRowType,
	TableColumnAlignType,
	TableColumnAlignClassType,
	TableSortType,
} from './types';

const props = defineProps<{
	columns: ColumnInterface[];
	rows: TableRowType[];
	sortBy: { column: string; direction: TableSortType };
	clickableRows?: boolean;
	selectableRows?: boolean;
	ariaLabel?: string;
	expandableRows?: boolean;
	preSelectedRows?: TableRowType[];
	hasTableFilters?: boolean;
}>();

const selectedRows = ref([]);
const allRowsSelected = ref<boolean>(false);
const moveCol = ref<{ from: number | null; to: number | null }>({ from: null, to: null });
const pageNumber = ref<number>(0);
const pageSize = ref<number>(25);

let headerToResize: HTMLHeadElement | null;
let resizedHeaderWidth = 0;
let mouseX = 0; // Get mouse X for col resizing.

const emit = defineEmits<{
	(e: 'clickRow', row: TableRowType): void;
	(e: 'clickHeader', column: ColumnInterface): void;
	(e: 'selectedRows', selectedRows: TableRowType[]): void;
}>();

watch(allRowsSelected, (newValue) => {
	selectedRows.value = newValue ? props.rows : [];
});

watch(selectedRows, () => emit('selectedRows', selectedRows.value));

const getColumnClass = (align: TableColumnAlignType | undefined): TableColumnAlignClassType => {
	switch (align) {
		case 'center':
			return 'align-center';
		case 'right':
			return 'align-right';
		default:
			return 'align-left';
	}
};

const getHeaderClass = (colIndex: number) => {
	const check = colIndex === moveCol.value.to && moveCol.value.to !== moveCol.value.from;
	return check ? 'drop-destination' : null;
};

const onMouseMove = (e: MouseEvent) => {
	if (headerToResize) {
		const dx = e.clientX - Number(mouseX);
		headerToResize.style.width = `${Number(resizedHeaderWidth) + dx}px`;
	}
};

const onMouseUp = (): void => {
	document.removeEventListener('mousemove', onMouseMove);
	document.removeEventListener('mouseup', onMouseUp);
};

const onMouseDown = (e: MouseEvent): void => {
	mouseX = e.clientX;
	headerToResize = (e.target as Element).parentElement;

	if (headerToResize) {
		const style = window.getComputedStyle(headerToResize);
		resizedHeaderWidth = parseInt(style.width, 10);

		document.addEventListener('mousemove', onMouseMove);
		document.addEventListener('mouseup', onMouseUp, true);
	}
};

const composedColumns = ref([...props.columns]);

const swapCols = () => {
	if (moveCol.value.from !== null && moveCol.value.to !== null) {
		[composedColumns.value[moveCol.value.from], composedColumns.value[moveCol.value.to]] = [
			composedColumns.value[moveCol.value.to],
			composedColumns.value[moveCol.value.from],
		];
	}
	moveCol.value = { from: null, to: null };
};

/** Logic for expandable columns here */
const expandedRowIndex = ref();

const onExpandRow = (rowIndex: number): void => {
	if (expandedRowIndex.value === rowIndex) {
		expandedRowIndex.value = null;
	} else {
		expandedRowIndex.value = rowIndex;
	}
};

const getPageNumberFromUrl = (): number => {
	const route = useRoute();
	const { query } = route || {};
	const number = query ? query.page : null;
	return number ? Number(number) - 1 : 0;
};

const getPageSizeFromUrl = (): number => {
	const route = useRoute();
	const { query } = route || {};
	const size = query ? query.limit : null;
	return size ? Number(size) : 0;
};

onMounted(() => {
	if (props.preSelectedRows) selectedRows.value = props.preSelectedRows;
	composedColumns.value = [...props.columns];
	composedColumns.value.unshift({ id: 'rowNumber', label: '#' });
	pageNumber.value = getPageNumberFromUrl();
	pageSize.value = getPageSizeFromUrl();
});

watch(
	() => props.preSelectedRows,
	() => {
		selectedRows.value = props.preSelectedRows;
	}
);

const handleFilterColumns = (filteredColumns: Array<{ id: string; label: string }>) => {
	composedColumns.value = filteredColumns;
};
</script>

<template>
	<div class="table-wrapper">
		<table
			:class="['table', { 'table--no-filters': !hasTableFilters }]"
			data-ts="table"
			:aria-label="ariaLabel || 'data table'"
		>
			<TableFilters
				v-if="hasTableFilters"
				:columns="columns"
				:aria-label="ariaLabel"
				@updateColumns="handleFilterColumns"
			/>
			<thead :class="{ 'head--no-filters': !hasTableFilters }">
				<tr>
					<th v-if="selectableRows" scope="col">
						<label>
							<input
								v-model="allRowsSelected"
								data-ts="select-all-checkbox"
								class="table__checkbox"
								type="checkbox"
							/>
						</label>
					</th>
					<TableHeader
						v-for="(column, idx) in composedColumns"
						:key="`col-head-${column.id}`"
						:column="column"
						:data-ts="`table-cell-${column.id}`"
						:class="[getColumnClass(column.align), getHeaderClass(idx)]"
						:idx="idx"
						:sort-by="sortBy"
						@move-from="(fromIndex) => (moveCol.from = fromIndex)"
						@move-to="(toIndex) => (moveCol.to = toIndex)"
						@swap-cols="swapCols"
						@click-header="$emit('clickHeader', column)"
						@resize-column="onMouseDown"
					/>
					<th v-if="expandableRows" scope="col">
						<span style="opacity: 0">Arrow</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<template v-for="(row, rowIndex) in rows" :key="`row-${rowIndex}`">
					<tr
						:data-ts="`table-row-${rowIndex}`"
						:class="[
							'table__row',
							{
								'table__row--clickable':
									clickableRows || (expandableRows && row.subRows.length),
							},
							{ 'table__row--expanded': rowIndex === expandedRowIndex },
						]"
						:tabindex="clickableRows ? 0 : undefined"
						@click="
							(clickableRows && $emit('clickRow', row)) ||
								(expandableRows && row.subRows.length && onExpandRow(rowIndex))
						"
						@keydown.enter="clickableRows && $emit('clickRow', row)"
					>
						<td v-if="selectableRows">
							<label class="table__checkbox">
								<input
									v-model="selectedRows"
									:data-ts="`row-${rowIndex}-checkbox`"
									type="checkbox"
									:value="row"
									@click.stop
								/>
							</label>
						</td>
						<td
							v-for="(column, idx) in composedColumns"
							:key="`column-body-${idx}`"
							:data-ts="`table-cell-${rowIndex}-${column.id}`"
							class="table__row__data"
							:class="[getColumnClass(column.align), column.class]"
						>
							<slot :name="column.id" :data="row">
								<span v-if="column.id === 'rowNumber'"
									>{{ pageNumber * pageSize + rowIndex + 1 }}
								</span>
								<span v-else>{{ row[column.id] ?? '-' }}</span>
							</slot>
						</td>
						<td v-if="expandableRows">
							<svg
								v-if="row.subRows.length"
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								:class="{ 'rotate-180': rowIndex === expandedRowIndex }"
							>
								<path
									d="M16.293 9.293 12 13.586 7.707 9.293l-1.414 1.414L12 16.414l5.707-5.707z"
								></path>
							</svg>
							<span v-else />
						</td>
					</tr>
					<template v-if="expandedRowIndex === rowIndex">
						<tr
							v-for="(subRow, subRowIndex) in row.subRows"
							:key="`subRow-${subRowIndex}`"
							class="table__sub-row"
						>
							<td
								v-for="(column, idx) in composedColumns"
								:key="`sub-col-${idx}`"
								class="table__row__data"
								:class="getColumnClass(column.align)"
							>
								<slot :name="column.id" :data="{ ...subRow, isSubRow: true }">
									{{ subRow[column.id] ?? '-' }}
								</slot>
							</td>
							<td />
						</tr>
					</template>
				</template>
			</tbody>
		</table>
	</div>
</template>

<style scoped lang="scss">
$table-row-height: 60px;
$table-filters-height: 40px;

.table-wrapper {
	overflow-x: auto;

	.table {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0;
		border-left: 1px solid $color-neutral-200;
		border-right: 1px solid $color-neutral-200;
		border-bottom: 1px solid $color-neutral-200;
		border-radius: 0 0 10px 10px;
		background-color: $color-white;

		&--no-filters {
			border-top: 1px solid $color-neutral-200;
			border-radius: 10px;
		}

		caption {
			background-color: $color-white;
			min-height: $table-filters-height;
			border-radius: 10px 10px 0 0;
			border-left: 1px solid $color-neutral-200;
			border-right: 1px solid $color-neutral-200;
			border-top: 1px solid $color-neutral-200;

			border-bottom: 1px solid $color-neutral-100;
		}

		th,
		td {
			height: $table-row-height;
			text-align: left;
			padding: 8px 16px;
		}

		thead > tr > th {
			height: calc($table-row-height - 10px);
			white-space: nowrap;
			user-select: none;
			-webkit-user-select: none;
			position: relative;
			border-bottom: 2px solid transparent;
			border-bottom: 1px solid $color-neutral-100;
			color: $color-neutral-500;
			font-weight: $font-weight-medium;
			font-size: $font-size-sm;
			background-color: $color-neutral-50;
		}

		.head--no-filters > tr > th {
			background-color: $color-gray-50;

			&:first-of-type {
				border-top-left-radius: 10px;
			}

			&:last-of-type {
				border-top-right-radius: 10px;
			}
		}

		tbody > tr > td {
			color: black;
			font-size: $font-size;
			border-bottom: $border-width-1 $border-style $color-neutral-100;
		}

		tr:last-child td:first-child {
			border-bottom-left-radius: 10px;
		}

		tr:last-child td:last-child {
			border-bottom-right-radius: 10px;
		}

		&__row {
			line-height: 1.5;
			position: relative;
			overflow: hidden;

			&--clickable {
				@media (hover: hover) {
					&:hover,
					&:focus-visible {
						cursor: pointer;
						background-color: $color-neutral-50;
					}
				}

				&:active {
					background-color: $color-neutral-50;
				}
			}

			&--expanded {
				background-color: $color-neutral-100;
				font-weight: $font-weight-medium;
			}
		}

		&__sub-row {
			height: 60px;
			font-size: 12px;
			background-color: $color-neutral-50;
		}
	}

	.align-center {
		text-align: center !important;
	}

	.align-right {
		text-align: right !important;
	}

	.drop-destination {
		border-bottom: 2px solid $color-primary !important;
	}
}

@media screen and (max-width: 1600px) {
	.table-wrapper {
		.table {
			tbody > tr > td,
			thead > tr > th {
				font-size: $font-size;
			}
		}
	}
}
</style>
